import { useRef, useState } from "react";
import { Paperclip, X, FilePdf, Image as ImageIcon, FileText } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/* v3.2.4 · Shared attachment uploader for jobs & proposals.
   Enforces a hard 5-file / 15 MB / safe-content-type policy client-side
   too — matches the server whitelist (PDF, PNG, JPEG, GIF, WebP, TXT).

   Props:
     endpoint   — "/jobs/upload-attachment" or "/proposals/upload-attachment"
     kind       — "job" | "proposal"  (drives the download URL prefix)
     value      — [{ref, filename, content_type, size_bytes}, ...]
     onChange   — (nextValue) => void
     testid     — data-testid prefix (defaults per kind)
     readOnly   — disables the picker; useful for view-only cards */
export default function AttachmentUploader({
  endpoint, kind, value = [], onChange, testid, readOnly = false,
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const prefix = testid || `${kind}-attach`;
  const MAX = 5;

  const iconFor = (ct) =>
    (ct || "").startsWith("image/") ? ImageIcon
    : ct === "application/pdf"       ? FilePdf
    : FileText;

  const pick = () => inputRef.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (value.length >= MAX) {
      toast.error(`At most ${MAX} attachments allowed.`);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange([...value, data]);
      toast.success(`Attached ${data.filename}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };
  const remove = (i) => onChange(value.filter((_, x) => x !== i));

  return (
    <div className="space-y-2" data-testid={`${prefix}-uploader`}>
      {!readOnly && (
        <div>
          <input ref={inputRef} type="file" hidden onChange={onFile}
            accept="application/pdf,image/png,image/jpeg,image/webp,image/gif,text/plain"
            data-testid={`${prefix}-file-input`} />
          <button type="button" onClick={pick} disabled={busy || value.length >= MAX}
            className={`px-3 py-1.5 text-xs font-mono border inline-flex items-center gap-1 ${busy || value.length >= MAX
              ? "border-white/10 text-gray-500"
              : "border-[#00E5FF] text-[#00E5FF] hover:bg-[#00E5FF]/10"}`}
            data-testid={`${prefix}-pick-btn`}>
            <Paperclip size={12} /> {busy ? "uploading…" : "attach file"}
          </button>
          <span className="ml-2 text-[10px] font-mono text-gray-500">
            PDF · PNG · JPEG · GIF · WebP · TXT · 15 MB / file · max {MAX} — {value.length}/{MAX} used
          </span>
        </div>
      )}
      {value.length > 0 && (
        <ul className="space-y-1" data-testid={`${prefix}-list`}>
          {value.map((a, i) => {
            const Icon = iconFor(a.content_type);
            const dl = `${api.defaults.baseURL}/${kind}s/attachments/${a.ref}`;
            const kb = (a.size_bytes || 0) / 1024;
            return (
              <li key={a.ref || i} className="flex items-center justify-between gap-2 px-2 py-1.5 border border-white/10 bg-[#12151C] text-xs font-mono"
                  data-testid={`${prefix}-item-${i}`}>
                <a href={dl} target="_blank" rel="noreferrer"
                   className="flex items-center gap-2 text-[#00E5FF] hover:underline min-w-0"
                   data-testid={`${prefix}-dl-${i}`}>
                  <Icon size={14} weight="fill" className="shrink-0" />
                  <span className="truncate">{a.filename}</span>
                  <span className="text-gray-500 text-[10px] shrink-0">
                    · {kb < 1000 ? `${kb.toFixed(0)} KB` : `${(kb/1024).toFixed(1)} MB`}
                  </span>
                </a>
                {!readOnly && (
                  <button type="button" onClick={() => remove(i)}
                    className="text-[#FF3B30] hover:text-[#FF6B60] shrink-0"
                    data-testid={`${prefix}-remove-${i}`}
                    title="Remove">
                    <X size={12} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
